import { useState, useEffect } from 'react';
import { supabase } from '@/lib/supabase';
import Badge from '@/components/base/Badge';
import Modal from '@/components/base/Modal';

interface AlertaFactura {
  id: number;
  folio: string;
  cliente: string;
  clientId: number;
  monto: number;
  diasVencido: number;
  fechaVencimiento: string;
  prioridad: string;
  totalAmount: number;
}

interface AlertaCredito {
  cliente: string;
  limiteCredito: number;
  saldoActual: number;
  porcentajeUsado: number;
}

export default function CollectionAlerts() {
  const [alertas, setAlertas] = useState<AlertaFactura[]>([]);
  const [creditoAlertas, setCreditoAlertas] = useState<AlertaCredito[]>([]);
  const [loading, setLoading] = useState(true);
  const [clientPhoneMap, setClientPhoneMap] = useState<Map<number, string>>(new Map());
  const [clientEmailMap, setClientEmailMap] = useState<Map<number, string>>(new Map());
  const [showPhoneModal, setShowPhoneModal] = useState(false);
  const [phoneInput, setPhoneInput] = useState('');
  const [phoneError, setPhoneError] = useState('');
  const [selectedAlerta, setSelectedAlerta] = useState<AlertaFactura | null>(null);

  const formatCurrency = (val: number) =>
    new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN', minimumFractionDigits: 0, maximumFractionDigits: 6 }).format(val);

  useEffect(() => {
    fetchAlertas();
  }, []);

  async function fetchAlertas() {
    try {
      const { data: invoices, error: invErr } = await supabase
        .from('erp_invoices')
        .select('id, folio, client_id, due_date, pending_balance, total_amount')
        .eq('status', 'overdue')
        .order('due_date', { ascending: true });

      if (invErr) throw invErr;

      const clientIds = [...new Set((invoices || []).map(i => i.client_id))];
      let clientMap: Map<number, { name: string }> = new Map();

      if (clientIds.length > 0) {
        const [clientsRes, contactsRes] = await Promise.all([
          supabase.from('erp_clients').select('id, company_name').in('id', clientIds),
          supabase.from('erp_contacts').select('client_id, phone, email, department').in('client_id', clientIds),
        ]);

        if (clientsRes.data) {
          clientMap = new Map(clientsRes.data.map(c => [c.id, { name: c.company_name }]));
        }

        const phoneMap = new Map<number, string>();
        const emailMap = new Map<number, string>();

        (contactsRes.data || []).forEach((c: { client_id: number; phone: string | null; email: string | null; department: string | null }) => {
          if (c.phone && !phoneMap.has(c.client_id)) {
            phoneMap.set(c.client_id, c.phone);
          } else if (c.phone && c.department === 'Finanzas') {
            phoneMap.set(c.client_id, c.phone);
          }
          if (c.email && !emailMap.has(c.client_id)) {
            emailMap.set(c.client_id, c.email);
          } else if (c.email && c.department === 'Finanzas') {
            emailMap.set(c.client_id, c.email);
          }
        });

        setClientPhoneMap(phoneMap);
        setClientEmailMap(emailMap);
      }

      const hoy = new Date();
      const facturas: AlertaFactura[] = (invoices || []).map(inv => {
        const fechaVenc = new Date(inv.due_date);
        const dias = Math.ceil((hoy.getTime() - fechaVenc.getTime()) / (1000 * 60 * 60 * 24));
        let prioridad = 'media';
        if (dias > 10) prioridad = 'critica';
        else if (dias > 5) prioridad = 'alta';
        const clientData = clientMap.get(inv.client_id);
        return {
          id: inv.id,
          folio: inv.folio,
          cliente: clientData?.name || 'Cliente',
          clientId: inv.client_id,
          monto: Number(inv.pending_balance),
          diasVencido: dias,
          fechaVencimiento: inv.due_date,
          prioridad,
          totalAmount: Number(inv.total_amount),
        };
      });

      setAlertas(facturas);

      const { data: clientesCred } = await supabase
        .from('erp_clients')
        .select('company_name, credit_limit, current_balance')
        .eq('credit_status', '30days')
        .gt('current_balance', 0);

      if (clientesCred) {
        const creditos: AlertaCredito[] = clientesCred
          .map(c => ({
            cliente: c.company_name,
            limiteCredito: Number(c.credit_limit),
            saldoActual: Number(c.current_balance),
            porcentajeUsado: Number(c.credit_limit) > 0 ? (Number(c.current_balance) / Number(c.credit_limit)) * 100 : 0,
          }))
          .filter(c => c.porcentajeUsado > 80)
          .sort((a, b) => b.porcentajeUsado - a.porcentajeUsado)
          .slice(0, 4);
        setCreditoAlertas(creditos);
      }
    } catch (err) {
      console.error('Error fetching alerts:', err);
    } finally {
      setLoading(false);
    }
  }

  function getWhatsAppMessage(a: AlertaFactura) {
    const fechaVenc = new Date(a.fechaVencimiento + 'T00:00:00').toLocaleDateString('es-MX');
    return `Hola, le recordamos que la factura ${a.folio} por un monto de ${formatCurrency(a.totalAmount)} correspondiente a sus servicios venció el ${fechaVenc} (${a.diasVencido} días de mora). Agradecemos nos comparta su comprobante de pago. Saludos.`;
  }

  function handleWhatsApp(a: AlertaFactura) {
    const phone = clientPhoneMap.get(a.clientId);
    if (phone) {
      const cleanPhone = phone.replace(/[\s\+\-\(\)]/g, '');
      const msg = getWhatsAppMessage(a);
      window.open(`https://wa.me/${cleanPhone}?text=${encodeURIComponent(msg)}`, '_blank');
    } else {
      setSelectedAlerta(a);
      setPhoneInput('');
      setPhoneError('');
      setShowPhoneModal(true);
    }
  }

  function handleWhatsAppWithPhone() {
    const cleanPhone = phoneInput.replace(/[\s\+\-\(\)]/g, '');
    if (!cleanPhone || cleanPhone.length < 10) {
      setPhoneError('Ingrese un número de teléfono válido (mínimo 10 dígitos)');
      return;
    }
    if (!selectedAlerta) return;
    const msg = getWhatsAppMessage(selectedAlerta);
    window.open(`https://wa.me/${cleanPhone}?text=${encodeURIComponent(msg)}`, '_blank');
    setShowPhoneModal(false);
    setSelectedAlerta(null);
  }

  function handleEmail(a: AlertaFactura) {
    const email = clientEmailMap.get(a.clientId);
    const subject = `Recordatorio de Pago - Factura ${a.folio}`;
    const body = getWhatsAppMessage(a);
    if (email) {
      window.open(`mailto:${email}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`, '_blank');
    } else {
      const fallback = window.prompt(`No hay correo registrado para ${a.cliente}. Ingrese el correo del cliente:`, '');
      if (fallback && fallback.includes('@')) {
        window.open(`mailto:${fallback}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`, '_blank');
      }
    }
  }

  if (loading) {
    return (
      <div className="bg-white rounded-xl border border-background-200 p-6">
        <div className="flex items-center justify-center h-48">
          <div className="w-5 h-5 border-2 border-primary-500 border-t-transparent rounded-full animate-spin"></div>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="bg-white rounded-xl border border-background-200 overflow-hidden">
        <div className="px-5 py-4 border-b border-background-100 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-red-50 flex items-center justify-center">
              <i className="ri-alert-line text-red-500 text-sm"></i>
            </div>
            <h3 className="text-sm font-semibold text-foreground-900">Alertas de Cobranza</h3>
          </div>
          <span className="text-xs font-medium text-foreground-500 bg-background-100 px-2 py-1 rounded-md">
            {alertas.length} facturas vencidas
          </span>
        </div>
        <div className="divide-y divide-background-100">
          {alertas.slice(0, 5).map((alerta) => (
            <div key={alerta.id} className="px-5 py-3.5 hover:bg-background-50 transition-colors">
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-xs font-mono text-foreground-500">{alerta.folio}</span>
                    <Badge
                      variant={alerta.prioridad === 'critica' ? 'danger' : alerta.prioridad === 'alta' ? 'warning' : 'neutral'}
                    >
                      {alerta.prioridad === 'critica' ? 'Crítica' : alerta.prioridad === 'alta' ? 'Alta' : 'Media'}
                    </Badge>
                  </div>
                  <p className="text-sm font-semibold text-foreground-800 truncate">{alerta.cliente}</p>
                  <div className="flex items-center gap-3 mt-1">
                    <span className="text-xs text-foreground-500">
                      Vencimiento: {new Date(alerta.fechaVencimiento + 'T00:00:00').toLocaleDateString('es-MX')}
                    </span>
                    <span className="text-xs font-bold text-red-600">{alerta.diasVencido} días vencido</span>
                  </div>
                </div>
                <div className="flex flex-col items-end gap-2">
                  <span className="text-sm font-bold text-foreground-900 whitespace-nowrap">{formatCurrency(alerta.monto)}</span>
                  <div className="flex gap-1">
                    <button
                      onClick={() => handleWhatsApp(alerta)}
                      className="w-7 h-7 rounded-md bg-green-50 hover:bg-green-100 flex items-center justify-center cursor-pointer transition-colors"
                      title="Enviar WhatsApp"
                    >
                      <i className="ri-whatsapp-line text-green-600 text-xs"></i>
                    </button>
                    <button
                      onClick={() => handleEmail(alerta)}
                      className="w-7 h-7 rounded-md bg-background-100 hover:bg-background-200 flex items-center justify-center cursor-pointer transition-colors"
                      title="Enviar Correo"
                    >
                      <i className="ri-mail-line text-foreground-500 text-xs"></i>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ))}
          {alertas.length === 0 && (
            <div className="px-5 py-8 text-center">
              <p className="text-sm text-foreground-500">No hay facturas vencidas</p>
            </div>
          )}
        </div>
        {creditoAlertas.length > 0 && (
          <div className="px-5 py-3 border-t border-background-100 bg-background-50">
            <h4 className="text-xs font-semibold text-foreground-700 mb-2">Clientes cerca del límite de crédito</h4>
            <div className="space-y-2">
              {creditoAlertas.map((item) => (
                <div key={item.cliente} className="flex items-center justify-between gap-3">
                  <span className="text-xs text-foreground-600 truncate flex-1">{item.cliente}</span>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <div className="w-24 h-1.5 rounded-full bg-background-200 overflow-hidden">
                      <div
                        className={`h-full rounded-full transition-all ${item.porcentajeUsado > 90 ? 'bg-red-500' : item.porcentajeUsado > 80 ? 'bg-amber-500' : 'bg-emerald-500'}`}
                        style={{ width: `${item.porcentajeUsado}%` }}
                      ></div>
                    </div>
                    <span className="text-xs font-semibold text-foreground-700 w-10 text-right">{item.porcentajeUsado.toFixed(0)}%</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      <Modal open={showPhoneModal} onClose={() => { setShowPhoneModal(false); setSelectedAlerta(null); }} title="Ingresar Teléfono">
        {selectedAlerta && (
          <div className="space-y-4">
            <div className="bg-amber-50 rounded-lg p-3">
              <p className="text-xs text-amber-700">
                <strong>{selectedAlerta.cliente}</strong> no tiene un número de teléfono registrado. Ingrese el número para enviar el recordatorio por WhatsApp.
              </p>
            </div>
            <div className="bg-background-50 rounded-lg p-3">
              <p className="text-xs text-foreground-600 mb-2 font-medium">Mensaje a enviar:</p>
              <p className="text-[11px] text-foreground-500 italic leading-relaxed">{getWhatsAppMessage(selectedAlerta)}</p>
            </div>
            <div>
              <label className="block text-xs font-semibold text-foreground-700 mb-1">Número de WhatsApp</label>
              <input
                type="tel"
                value={phoneInput}
                onChange={(e) => { setPhoneInput(e.target.value); setPhoneError(''); }}
                className="w-full px-3 py-2 text-sm border border-background-200 rounded-lg focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 text-foreground-800"
                placeholder="Ej: +52 1 55 1234 5678"
              />
              {phoneError && (
                <p className="text-xs text-red-600 mt-1">{phoneError}</p>
              )}
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <button
                onClick={() => { setShowPhoneModal(false); setSelectedAlerta(null); }}
                className="px-4 py-2 text-xs font-medium text-foreground-600 bg-background-100 rounded-lg hover:bg-background-200 cursor-pointer transition-colors whitespace-nowrap"
              >
                Cancelar
              </button>
              <button
                onClick={handleWhatsAppWithPhone}
                className="px-4 py-2 text-xs font-semibold text-white bg-green-600 rounded-lg hover:bg-green-700 cursor-pointer transition-colors whitespace-nowrap flex items-center gap-1.5"
              >
                <i className="ri-whatsapp-line"></i>
                Enviar WhatsApp
              </button>
            </div>
          </div>
        )}
      </Modal>
    </>
  );
}